import { useEffect, useMemo, useState } from 'react';
import { SALUTATION_EMPTY_LABEL, SALUTATION_OPTIONS, SALUTATION_SEGMENT_WIDTH } from '../../../../lib/salutations';
import { useAuth, usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdNumber, TdSeamless, TdSelect } from '../../../../components/inline-add-table/cells';
import { Modal } from '../../../../components/Modal';
import { Stepper } from '../../../../components/Stepper';
import { Tooltip } from '../../../../components/Tooltip';
import { AddressPicker, type AddressValue } from '../../../../components/inputs/AddressPicker';
import { DateField } from '../../../../components/inputs/DateField';
import { FieldGroup } from '../../../../components/inputs/FieldGroup';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { PPA_EXPENSE_CLASS_LABEL, PPA_EXPENSE_CLASS_ORDER } from '../../../../lib/expenseClass';
import { itemIdentity } from '../../../../lib/itemIdentity';
import { EMPTY_MONEY, formatMoney } from '../../../../lib/formatMoney';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { issuancesApi } from '../issuanceApi';
import type {
  CreateIssuanceLine,
  IssuableItem,
  IssuableSource,
  IssuableUnit,
  IssuanceType,
} from '../issuanceTypes';
import {
  ISSUANCE_TYPE,
  ISSUANCE_TYPE_HINT,
  ISSUANCE_TYPE_LABEL,
  PROPERTY_CLASSIFICATION,
  PROPERTY_CLASSIFICATION_HINT,
  PROPERTY_CLASSIFICATION_LABEL,
  isCustody,
  methodsForKind,
} from '../issuanceTypes';
import type { PropertyClassification } from '../issuanceTypes';
import { ITEM_KIND_LABEL } from '../inventoryTypes';
import type { InventoryItemKind } from '../inventoryTypes';
import { pick, sampleIssuance } from '../utils/sampleIssuance';

type Row = {
  key: string;
  inventoryItemId: string;
  storageFacilityId: string;
  stockUnitId: string;
  quantity: string;
};

const EMPTY_ROW = (): Row => ({
  key: crypto.randomUUID(),
  inventoryItemId: '',
  storageFacilityId: '',
  stockUnitId: '',
  quantity: '1',
});

/**
 * The reference’s three, in its order.
 *
 * <b>The Summary is NOT a re-statement of what was just typed</b>, which is the shape this
 * product refuses: it COLLECTS what neither of the first two steps asks for — who signs, the
 * ICS/PAR document header, and the reason — and it is where the confirm tick lands because it
 * is genuinely last.
 */
/**
 * The steps a method actually has. TARGET is the reference’s own `needsLoanDetailStep`: it is
 * drawn only where there is an external party to name, so a dispense and an assign — which
 * go to an employee — never meet it.
 */
/*
  METHOD AND ITEMS ARE ONE STEP — product call, 29 Sep 2026, and it diverges from the source,
  which asks them on two. The method decides which items the picker may offer at all, so the
  two questions are one decision: what is leaving the store, and under what.
*/
const METHOD_STEPS = ['Items', 'Summary'] as const;
const LOAN_STEPS = ['Items', 'Loan Detail', 'Summary'] as const;
type StepKey = (typeof LOAN_STEPS)[number];

/**
 * Release stock out of a store.
 *
 * **Stock moves when this saves.** There is no draft, so the confirm tick is certifying an act
 * rather than a record — which is why the last step states what is about to leave before it.
 *
 * **The item picker only ever offers what can actually be issued.** It is fed by
 * `issuable-items`, which drops the wrong kind, anything with nothing available, and date-lapsed
 * lots — so a reader cannot pick a row the server will refuse. Choosing the method first is what
 * makes that possible, which is why it leads.
 */
export function IssuanceModal({
  open,
  onClose,
  onSaved,
  initialItemId = null,
  initialKind = null,
  initialUnitId = null,
  initialAccountablePersonnelId = null,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Saved — and WHICH GROUPS moved with it, as item TYPE ids.
   *
   * Stock Levels groups its rows by item type and fetches one group's figures at a time, so a
   * caller that reloads the whole table on a save throws away the tab, the scroll and every
   * other group's open state to report a change to one of them. A caller with no such table
   * ignores the argument, which is why it costs nothing.
   *
   * **Every type on the slip, not the one it was seeded with.** A reader may add lines beside
   * the item they opened this for, and a scoped reload that named only the seed would leave
   * the others showing figures that have just moved — which is worse than reloading all of it.
   */
  onSaved: (movedTypeIds: string[]) => void;
  /**
   * The item this form was opened FOR, where it was opened from a row that names one.
   *
   * It seeds the first line and does not lock it: the reader chose the item by choosing whose
   * menu to open, and may still add lines beside it. Absent from every other caller, which is
   * the absent-prop idiom — a surface with no item in hand seeds nothing.
   */
  initialItemId?: string | null;

  /**
   * The serialized unit the form was opened FOR, where the row that opened it names one.
   *
   * Absent from every caller that opened on an ITEM, which is the absent-prop idiom: a surface
   * with no unit in hand seeds none. It requires `initialItemId` to mean anything, because
   * the unit picker is fed by the item above it.
   */
  initialUnitId?: string | null;

  /**
   * THE KIND THE READER PRESSED ISSUE ON, which narrows the METHOD picker.
   *
   * <b>The method leads the form and the item feed narrows under it</b> — which is right for
   * a form opened cold, and backwards the moment the reader has already pointed at a row: the
   * item is decided, so it is the method that has a wrong answer to rule out. A consumable is
   * dispensed and nothing else; an asset and a licence are assigned or lent and never
   * dispensed, which is <c>IssuanceMethods.PermitsKind</c> and has always been refused by the
   * server.
   *
   * <b>Absent means opened cold</b> — the absent-prop idiom — and every method is offered.
   */
  initialKind?: InventoryItemKind | null;

  /**
   * THE EMPLOYEE THE FORM WAS OPENED FOR — the personnel profile's Issued Assets panel.
   *
   * It seeds the accountable officer (and Received by, which follows it) and opens the form on
   * ASSIGN, the method that hands property into one employee's custody: a dispense names no
   * accountable officer at all, so opening on it would hide the very field this seeded. Neither
   * is locked — the reader may still pick a lend or another officer. Absent from every other
   * caller, which is the absent-prop idiom.
   */
  initialAccountablePersonnelId?: string | null;
}) {
  const canReadPersonnel = usePermission('personnel.read');

  const [step, setStep] = useState(0);

  /*
    THE METHODS THIS FORM MAY OFFER.

    An EMPTY result falls back to every method rather than drawing a picker with nothing in
    it. Real property is the only kind that permits none, and no caller offers Issue on one —
    the row kebab omits the entry outright — so this is a floor rather than a path, and a
    form that could not state a method would be worse than one the server then refuses.
  */
  const methods = useMemo(() => {
    const permitted = methodsForKind(initialKind);
    return permitted.length > 0 ? permitted : Object.values(ISSUANCE_TYPE);
  }, [initialKind]);

  /*
    SEEDED ONCE, from the narrowed list — the modal is mounted only while it is open, so a
    fresh opening re-runs this and there is no effect to keep in step with the prop.
  */
  const [issuanceType, setIssuanceType] = useState<IssuanceType>(() =>
    initialAccountablePersonnelId !== null && methods.includes(ISSUANCE_TYPE.assign)
      ? ISSUANCE_TYPE.assign
      : methods[0],
  );

  /*
    WHICH GOVERNMENT FORM IS BEING SIGNED.

    Seeded EMPTY rather than to either side: an ICS and a PAR are opposite claims about what
    class of property this is, so a pre-selected one records an answer nobody gave while
    looking answered. That is the required-yes/no rule, and it is what the reference’s own
    "Select ICS or PAR" placeholder says too.
  */
  const [acknowledgementType, setAcknowledgementType] = useState<PropertyClassification | ''>('');
  const { user } = useAuth();

  const [accountableId, setAccountableId] = useState(initialAccountablePersonnelId ?? '');

  /*
    THERE IS NO RECEIVED BY — product call, 06 Oct 2026, in the reader own words: *remove
    the received by, move the issued by in line with accountable*.

    The officer who signs for the stock is the one who collects it in every ordinary case,
    which is what the field defaulted to; what is left of it is the runner case, and the
    reader has decided it is not worth a control. The COLUMN and the request field both
    stay, so restoring the question is an edit to this form rather than a migration — the
    call the competency-development block already takes. The seven slips written before this
    keep whoever they recorded; nothing is cleared and nothing is destroyed.

    The PROFILE drops its two rows in the same change, or every slip written from here on
    would carry an em dash under a heading naming somebody who was never asked for.
  */
  const [issuedById, setIssuedById] = useState('');
  const [externalParty, setExternalParty] = useState('');

  /*
    THE CONTACT IS A PERSON, so the form collects a NAME and the row stores an id.

    Nothing here ever reads one back — the detail carries a `PersonId` and the profile
    resolves it, which is how every person in this product reaches a screen. These four are
    write-only, which is why the create request has them and the detail does not.
  */
  const [contactSalutation, setContactSalutation] = useState('');
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactMiddleName, setContactMiddleName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactSuffix, setContactSuffix] = useState('');

  const [address, setAddress] = useState<AddressValue>({
    province: null,
    cityMunicipality: null,
    barangay: null,
  });

  const [streetAddress, setStreetAddress] = useState('');

  // The ICS / PAR printable-form header. Custody only, and the server refuses it otherwise.
  /*
    THERE IS NO LGU FIELD EITHER — product call, 06 Oct 2026, in the reader own words:
    *remove the LGU/Office, instead auto detect the unit of the accountable person*.

    The server derives it from the accountable officer own org unit and the REQUEST has no
    field for it at all — a permanent snapshot at the head of a signed government form that
    a caller could name is one a caller could misname, which is the same argument the two
    position snapshots already run on.
  */
  const [fundCluster, setFundCluster] = useState('');
  const [externalContact, setExternalContact] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [purpose, setPurpose] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<Row[]>([EMPTY_ROW()]);

  const [items, setItems] = useState<IssuableItem[]>([]);
  const [sources, setSources] = useState<Record<string, IssuableSource[]>>({});
  const [units, setUnits] = useState<Record<string, IssuableUnit[]>>({});

  /*
    EVERY issuable unit for the chosen method, in one call — what the Item column picks from.

    A serialized issue is one named unit, so the row IS a unit; the picker therefore has to be
    able to list units before anything has been chosen, which a per-item feed cannot do.
  */
  const [allUnits, setAllUnits] = useState<IssuableUnit[]>([]);
  /*
    An employee as the three signatory pickers need them: who they are, where they sit, and
    which account they sign in as. No name is stored — `name` is resolved per opening through
    the identity service, which is why this is state rather than part of the personnel row.
  */
  const [staff, setStaff] = useState<
    { id: string; name: string; unit: string | null; userId: string | null }[]
  >([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const custody = isCustody(issuanceType);

  /*
    The item feed is keyed on the METHOD, so changing it reloads and clears the lines: an asset
    picked under Assign is not offerable under Dispense, and leaving the rows would post items
    the server refuses for a reason the reader could not see.
  */
  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoadingItems(true);

    /*
      BOTH FEEDS, together — the quantity items and every issuable UNIT. The Item column offers
      them as one list, because a row is one exact thing: a named unit where the item is
      serialized, and the item itself where stock is counted.
    */
    /*
      SCOPED BY THE FORM. Only stock recorded under the chosen class is offered, so a reader
      cannot pick a row the endpoint would then refuse — the reference’s own sentence.
    */
    const form = acknowledgementType === '' ? null : acknowledgementType;

    Promise.all([
      issuancesApi.issuableItems(issuanceType, form, controller.signal),
      issuancesApi.issuableUnitsFor(issuanceType, form, controller.signal),
    ])
      .then(([feed, serialized]) => {
        if (controller.signal.aborted) return;
        setItems(feed);
        setAllUnits(serialized);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingItems(false);
      });

    return () => controller.abort();
  }, [open, issuanceType, acknowledgementType]);

  /*
    The accountable picker resolves its NAMES through the identity service, in ONE batch call —
    the page of ids it is showing, never one request per row. That is the whole reason a
    personnel row carries a `personId` rather than a name.
  */
  useEffect(() => {
    if (!open || !canReadPersonnel) return;

    const controller = new AbortController();

    personnelApi
      .list({ pageSize: 100 }, controller.signal)
      .then(async (page) => {
        const names = await personsApi.resolve(
          // A record above this reader's access level arrives with NO personId — that absence
          // is the mask — so there is nothing to resolve. The option falls back to the employee
          // number below, which identifies the row without naming the person.
          page.items.map((p) => p.personId).filter((id): id is string => id != null),
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPersonId = new Map(names.map((n) => [n.personId, n.displayName]));

        setStaff(
          page.items.map((p) => ({
            id: p.id,
            name: (p.personId != null ? byPersonId.get(p.personId) : undefined) ?? p.employeeNumber,

            /*
              THE UNIT, stated under the name in every one of these three pickers. A list of
              display names alone is unusable the moment two employees share a surname, and the
              office somebody sits in is what a store clerk actually recognises them by.

              It is the SUBSTANTIVE post’s unit — the primary line of service — which is what the
              personnel list itself reports, so the two surfaces cannot disagree.
            */
            unit: p.orgUnitName ?? null,

            // What makes "Issued by = whoever is signed in" answerable. See the default below.
            userId: p.userId ?? null,
          })),
        );
      })
      // A failed picker load names no field, so it must not reach the save path's error state.
      .catch(() => undefined);

    return () => controller.abort();
  }, [open, canReadPersonnel]);

  /*
    ISSUED BY DEFAULTS TO WHOEVER IS SIGNED IN. Product call, 28 Sep 2026.

    The person releasing stock from the store is the person at the keyboard in every ordinary
    case, so asking them to find themselves in a list of a hundred names is asking for a fact
    the session already holds. It is a DEFAULT and not a lock: a clerk keying a slip somebody
    else released changes it.

    <b>Seeded once per opening, and only over an empty field</b> — the staff feed lands after
    the form opens, so this cannot be initial state, and re-applying it would overwrite a
    reader who had already answered.

    <b>IT RESOLVES TO NOTHING WHERE NO EMPLOYEE IS LINKED TO THE ACCOUNT, and on this database
    that is every one of them</b> — 3 active personnel records, 0 carrying a `UserId`, queried
    rather than assumed. The field is then correctly empty, and its own description is what
    says so: a default that silently does nothing is a control the reader cannot explain.

    <b>Matched case-INSENSITIVELY.</b> An Identity user id is a STRING rather than a Guid, so
    the two sides are free to differ in case — the column as stored, and the claim as the token
    carries it. A `===` there fails silently and looks exactly like an account that is not
    linked, which is the one failure this default cannot afford: both states read "Not
    recorded".
  */
  useEffect(() => {
    if (!open || user === null || issuedById !== '') return;

    const me = staff.find(
      (s) => s.userId !== null && s.userId.toLowerCase() === user.id.toLowerCase(),
    );

    if (me) setIssuedById(me.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, staff, user]);

  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  /*
    SEEDS the first line from the row this form was opened on, once the feed for the chosen
    method has landed.

    Applied only to a FIRST, UNTOUCHED row, so it never overwrites something the reader picked.
    A method change clears the lines and reloads the feed, which re-seeds — deliberately: the
    reader opened this form for that item, and switching from Dispense to Assign does not change
    which item they came for.

    <b>An item the chosen method cannot issue is simply not in the feed</b>, so nothing is seeded
    and the picker opens empty. That is the honest answer rather than a row the server refuses,
    and it is what `issuable-items` exists to make automatic.
  */
  useEffect(() => {
    if (!open || !initialItemId || !itemsById.has(initialItemId)) return;

    setRows((current) =>
      current.length === 1 && current[0].inventoryItemId === ''
        ? [{ ...current[0], inventoryItemId: initialItemId }]
        : current,
    );

    void (async () => {
      const feed = await loadFor(initialItemId);

      /*
        THE UNIT is seeded as well, where the row that opened this form named one — a reader who
        pressed Issue on a serial has already answered which one, and re-picking it from a list
        of forty is the question the seed exists to remove.

        <b>Only if the feed still offers it.</b> `issuable-items` drops a unit the chosen
        method cannot release, so a stale row seeds nothing rather than a value the server refuses — the
        same rule the item above it already follows.
      */
      if (initialUnitId === null || !feed.units.some((u) => u.id === initialUnitId)) return;

      setRows((current) =>
        current.length === 1
        && current[0].inventoryItemId === initialItemId
        && current[0].stockUnitId === ''
          ? [{ ...current[0], stockUnitId: initialUnitId, quantity: '1' }]
          : current,
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialItemId, initialUnitId, itemsById]);

  /**
   * The stores and the units one item can be drawn from, under the form being signed — loaded
   * once per item, when it is first picked, and HANDED BACK as well as cached.
   *
   * The return value is for Autogenerate, which has to fill a line in one gesture and cannot
   * wait a render for the state it just set. Every other caller ignores it.
   *
   * <b>`override` exists for AUTOGENERATE, and for nothing else.</b> That path SETS the form
   * and then immediately needs a feed scoped to it — and the state has not flushed, so the
   * closure still holds the previous value and the per-item caches still hold the previous
   * form's stock. Both are correct for the reader, who cannot act faster than a render.
   *
   * <b>It bypasses the cache deliberately.</b> `units` and `sources` are keyed on the item
   * alone, because the reader-facing path clears them whenever the form changes; a caller that
   * has not been through that clear has to go past them.
   */
  async function loadFor(
    itemId: string,
    override?: { form: PropertyClassification | null; item: IssuableItem },
  ): Promise<{ sources: IssuableSource[]; units: IssuableUnit[] }> {
    const item = override?.item ?? itemsById.get(itemId);
    if (!item) return { sources: [], units: [] };

    const form = override ? override.form : acknowledgementType === '' ? null : acknowledgementType;

    if (item.tracksSerial) {
      const held = override ? undefined : units[itemId];
      if (held) return { sources: [], units: held };

      const result = await issuancesApi.issuableUnits(itemId, form).catch(() => []);
      setUnits((current) => ({ ...current, [itemId]: result }));

      return { sources: [], units: result };
    }

    const held = override ? undefined : sources[itemId];
    if (held) return { sources: held, units: [] };

    const result = await issuancesApi.issuableSources(itemId, form).catch(() => []);
    setSources((current) => ({ ...current, [itemId]: result }));

    return { sources: result, units: [] };
  }

  /**
   * Fills every required field, so the primary action enables on the first try.
   *
   * <b>It resolves against the LIVE feeds</b> — a real issuable item, a real store that has
   * some of it, a real free unit, a real employee. Nothing here is invented, which is what
   * stops a generated form failing the server's own "it must exist and have stock" check.
   */
  function autogenerate() {
    const sample = sampleIssuance();

    setPurpose(sample.purpose);
    setNotes(sample.notes);

    /*
      THE FORM IS FILLED, because it is REQUIRED on a custody method and the contract is that
      the primary action enables on the first try.

      ICS rather than a coin toss. The two are not interchangeable — each scopes the feed to
      different stock — so a generator that picked at random would produce a different form on
      every click, which is the same reason the METHOD is never touched. ICS is the one most
      stock falls under, so it is the choice most likely to have something to issue.

      NEVER over an answer the reader has already given.
    */
    const form = acknowledgementType === '' ? PROPERTY_CLASSIFICATION.ics : acknowledgementType;
    if (custody && acknowledgementType === '') setAcknowledgementType(form);

    // The METHOD is never touched, so what the method requires is what gets filled.
    if (staff.length > 0) {
      // NEVER over the employee the form was opened for — the reader decided that by choosing
      // whose profile to press Create on, which is the generator's own "already decided" rule.
      const seeded = initialAccountablePersonnelId !== null;
      if (custody && !seeded) setAccountableId(pick(staff).id);

      // Optional, and the form can offer a real value — so it is filled, which is what
      // makes the generated record exercise the profile rather than leave it em dashes.
      setIssuedById(pick(staff).id);
    }

    /*
      THE FORM HEADER IS FILLED even though neither field is required.

      "The primary action enables" is the FLOOR of the contract, not the whole of it: the point
      is that the screens behind the form get exercised, and a generated slip whose profile
      reads em dashes under "ICS / PAR form details" exercises nothing.
    */
    if (custody) {
      setFundCluster(sample.fundCluster);
    }

    if (issuanceType === ISSUANCE_TYPE.lend) {
      setExternalParty(sample.externalParty);
      setExternalContact(sample.externalContact);
      setDueDate(sample.dueDate);

      /*
        THE CONTACT NAME IS OBVIOUSLY SYNTHETIC, because it lands in `rsys_pii`.

        Every other value this generator produces is plausible vocabulary, which is right for
        anything that stays in `rsys_`. A name does not stay: it is written to the one store
        whose purpose is holding real people, where a fabricated-but-realistic one is
        indistinguishable from a real one to whoever audits it later.

        NO MIDDLE NAME AND NO SUFFIX. Both are optional, and leaving them empty is what
        exercises the half of the form a reader most often leaves alone.
      */
      setContactSalutation(sample.contactSalutation);
      setContactFirstName(sample.contactFirstName);
      setContactLastName(sample.contactLastName);

      /*
        THE ADDRESS IS LEFT ALONE, and that is the resolve-against-the-live-catalog rule
        rather than an omission: the picker writes a path the address catalog actually holds,
        and an invented province is a value no search would ever have offered. The street line
        is free text and is filled.
      */
      setStreetAddress(sample.streetAddress);
    }

    void (async () => {
      /*
        A line the reader has already decided is left alone — including one SEEDED from the row
        this form was opened on. What is filled is whatever that line is still missing.
      */
      const first = rows[0];
      if (!first) return;

      /*
        FETCHED FOR THE CHOSEN FORM rather than read out of state.

        Setting the acknowledgement reloads the feed, and that reload has not landed by the
        time this runs — so `items` still holds whatever the previous form offered, and a line
        filled from it would name stock the save then refuses. One call answers it exactly.
      */
      const offered = custody
        ? await issuancesApi.issuableItems(issuanceType, form).catch(() => items)
        : items;

      const chosen = first.inventoryItemId || (offered.length > 0 ? pick(offered).id : '');
      if (chosen === '') return;

      const item = offered.find((i) => i.id === chosen) ?? itemsById.get(chosen);
      if (!item) return;

      // The form THIS call chose, never the one state is still holding.
      const feed = await loadFor(chosen, { form: custody ? form : null, item });

      if (item?.tracksSerial) {
        const unit = feed.units[0];
        if (!unit) return;

        setRow(first.key, { inventoryItemId: chosen, stockUnitId: first.stockUnitId || unit.id });
        return;
      }

      const source = feed.sources.find((s) => s.available > 0);
      if (!source) return;

      setRow(first.key, {
        inventoryItemId: chosen,
        storageFacilityId: first.storageFacilityId || source.storageFacilityId,
        quantity: first.quantity || '1',
      });
    })();
  }

  /*
    THE PICKED UNIT, resolved from the PER-ITEM feed first and the flat one second.

    <b>The order stopped mattering on 29 Sep 2026</b> and is kept rather than simplified. The
    merged cell draws from `allUnits`, so the unit a reader picks is always in the flat feed —
    `units[itemId]` is what `loadFor` returns for one line, and it is now only ahead of the
    fallback for a row seeded from a kebab before either feed has landed.

    Reading both is what keeps that seeded row resolvable, which is the whole job this does.
  */
  const unitsById = useMemo(() => new Map(allUnits.map((u) => [u.id, u])), [allUnits]);

  /**
   * ONE list for ONE cell — the item and the unit merged, which is what the feed comment above
   * has described since the two were loaded together.
   *
   * <b>A row is one exact thing.</b> A quantity item IS the answer; a serialized item is not,
   * because "Laptop" does not say which laptop. So a serialized item contributes one option per
   * free UNIT and a quantity item contributes itself, and the cell asks its question once.
   *
   * <b>The separator is ` — `</b>, which is what this product joins a qualifier to a name with
   * everywhere else — the education panel's `College — University of the Philippines`. It is
   * the anchor-merge rule reaching a form: one line, one header, one width, and the merged
   * field loses its own column rather than being printed twice.
   *
   * <b>The SERIAL and the SKU stay searchable.</b> They are in `keywords`, which `<Select>`
   * matches and does not draw — so a reader who knows the serial still finds the row, and
   * dropping the text from the index would quietly make the search match less than it did.
   *
   * <b>The value carries both ids</b>, joined by `::`. A composite is what lets one control
   * answer two fields without the cell holding state of its own; item ids are GUIDs, so the
   * separator cannot appear inside one.
   */
  const lineOptions = useMemo(
    () =>
      items.flatMap((item) => {
        /*
          THE LABEL IS THE PRODUCT, not the item type — product call, 06 Oct 2026, in the
          reader's own words: *in the issuance modal, concat the manufacturer/brand/model
          to destinguish the item*.

          `item.name` is the item TYPE's name, because `InventoryItem` has no name column
          at all — so a catalog holding five laptops offered "Laptop" five times and the
          only thing separating the options was a serial or a SKU nobody has memorised.

          THE JOIN IS `lib/itemIdentity`'s, which is the Stock Levels anchor's own and the
          purchase request picker's: a SPACE, because the three are one product's name as
          anybody writes it — `Lenovo ThinkPad X1 Gen 11`. The product's ` — ` joins a
          qualifier to a name and would read as separate facts.

          THE TYPE MOVES TO THE SUBLABEL, and only where the label is not already it —
          printing one string twice is what `<ProfileIdentity>` suppresses one surface
          along. The AVAILABLE count stays either way: the cell beside this asks for a
          quantity, and a number means nothing without what is left to draw on.
        */
        const identity = itemIdentity(item);

        if (!item.tracksSerial) {
          return [
            {
              value: `${item.id}::`,
              label: identity || item.name,
              sublabel: identity
                ? `${item.name} · ${item.available} available`
                : `${item.sku} · ${item.available} available`,

              /*
                THE SKU STAYS SEARCHABLE. It leaves the label where an identity replaces
                it, and a reader who knows it still finds the row — what is DISPLAYED
                narrows, what is SEARCHABLE must not.
              */
              keywords: item.sku,
            },
          ];
        }

        /*
          A serialized item with no free unit contributes NOTHING, and that is the feed's own
          answer rather than this list's: `issuable-items` already drops what cannot go out
          under the chosen method, so an item with nothing free is not in `items` either.
        */
        return allUnits
          .filter((unit) => unit.inventoryItemId === item.id)
          .map((unit) => ({
            value: `${item.id}::${unit.id}`,

            /*
              THE PRODUCT, then the serial. The identity is what says WHICH laptop and the
              serial is what says which ONE of them — so a reader scanning the list reads
              `Lenovo ThinkPad X1 Gen 11 — SN-0012` rather than five rows of `Laptop`.

              THE ` — ` SURVIVES HERE and the identity is joined with spaces INSIDE it.
              That is not two separators for one job: the three identity parts are one
              name, and the serial is a qualifier ON that name — which is exactly what the
              product's em dash joins. `College — University of the Philippines` is the
              same shape.

              THE STORE STAYS IN THE SUBLABEL, because it is a unit's own fact and the one
              the reader is choosing between two identical serials on. The TYPE is appended
              where the label is no longer it, so the governed name is still readable.
            */
            label: `${identity || item.name} — ${unit.serialNumber}`,
            sublabel: identity
              ? `${item.name} · ${unit.storageFacilityName ?? 'No store'}`
              : (unit.storageFacilityName ?? 'No store'),
            keywords: `${item.sku} ${unit.serialNumber}`,
          }));
      }),
    [items, allUnits],
  );

  /*
    A UNIT ALREADY NAMED ON ANOTHER LINE IS NOT OFFERED AGAIN.

    A serialized unit is one physical thing, so it can leave the store once — the server says
    so outright ("The same unit is on this issuance twice") and refuses the whole request. The
    picker drops it instead, which is where the reader can act on it: a duplicate that only
    surfaces on save is a form that let them build something it was always going to reject.

    **The row's OWN pick is never dropped.** `<Select>` resolves its label from its options, so
    a row whose value is missing from its own list falls back to the placeholder and reads as
    unanswered while still carrying the unit.

    A QUANTITY item is untouched: two lines drawing the same item from two stores is exactly
    what the source-per-line column is for, and what the server's own allocation expects.
  */
  const optionsFor = (row: Row) => {
    const takenElsewhere = new Set(
      rows.filter((r) => r.key !== row.key && r.stockUnitId !== '').map((r) => r.stockUnitId),
    );

    if (takenElsewhere.size === 0) return lineOptions;

    return lineOptions.filter((option) => {
      const unitId = option.value.split('::')[1] ?? '';

      return unitId === '' || !takenElsewhere.has(unitId);
    });
  };

  const unitOf = (row: Row) =>
    row.stockUnitId === ''
      ? undefined
      : ((units[row.inventoryItemId] ?? []).find((u) => u.id === row.stockUnitId) ??
        unitsById.get(row.stockUnitId));
  function setRow(key: string, patch: Partial<Row>) {
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  const lineValid = (row: Row) => {
    const item = itemsById.get(row.inventoryItemId);
    if (!item) return false;

    const quantity = Number(row.quantity);

    return item.tracksSerial
      ? row.stockUnitId !== ''
      : row.storageFacilityId !== '' && Number.isFinite(quantity) && quantity > 0;
  };

  /*
    THE STEPS ARE DERIVED FROM THE METHOD, and the index is CLAMPED IN RENDER rather than
    reset in an effect: switching Lend → Assign drops the Target step underneath a reader
    standing on it, and an index past the end would resolve to undefined and take the panel
    with it.
  */
  const needsLoanDetail = issuanceType === ISSUANCE_TYPE.lend;
  const steps: readonly StepKey[] = needsLoanDetail ? LOAN_STEPS : METHOD_STEPS;
  const safeStep = Math.min(step, steps.length - 1);
  const activeKey = steps[safeStep];

  /*
    ONE CHECK PER STEP, so Next gates on the step the reader is standing on rather than on
    the first one. That is the reference’s own `validateStep(activeKey)`.
  */
  const methodValid = !custody || acknowledgementType !== '';
  const itemsValid = rows.length > 0 && rows.every(lineValid);
  const loanDetailValid = !needsLoanDetail || (externalParty.trim() !== '' && dueDate !== '');
  const summaryValid = !custody || accountableId !== '';
  const stepValid =
    activeKey === 'Items'
      ? methodValid && itemsValid
      : activeKey === 'Loan Detail'
        ? loanDetailValid
        : true;
  /*
    THE TICK IS ON THE LAST STEP, and that is derived from the array rather than written as a
    number — adding the Summary moved it with no other change, which is the whole reason it
    was never a literal.
  */
  const isLastStep = safeStep === steps.length - 1;
  const canSave = methodValid && itemsValid && loanDetailValid && summaryValid && confirmed && !saving;

  /** Names the FIRST thing missing, on whichever step it is — never just "not saving". */
  const missing = !methodValid
    ? 'Items — choose the acknowledgement form, ICS or PAR.'
    : !itemsValid
      ? 'Items — every line needs an item, a source and a quantity.'
      : !loanDetailValid
        ? externalParty.trim() === ''
          ? 'Loan Detail — name the party the stock is lent to.'
          : 'Loan Detail — set the expected return date.'
        : !summaryValid
          ? 'Summary — choose the accountable employee.'
          : !confirmed
            ? 'Tick the confirmation to release the stock.'
            : null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    // Read once rather than eight times, so no two fields can disagree about the branch.
    const lent = issuanceType === ISSUANCE_TYPE.lend;

    const request = {
      issuanceType,

      // NULL on a non-custody method, which signs no form and whose lines the server refuses
      // an acknowledgement on rather than ignoring one.
      acknowledgementType: custody && acknowledgementType !== '' ? acknowledgementType : null,
      accountablePersonnelId: custody ? accountableId || null : null,
      // The reference names no receiving unit, so this form writes none. The column stays.
      receivingOrgUnitId: null,
      externalPartyName: issuanceType === ISSUANCE_TYPE.lend ? externalParty.trim() || null : null,
      externalPartyContact:
        issuanceType === ISSUANCE_TYPE.lend ? externalContact.trim() || null : null,

      /*
        SENT AS A NAME, STORED AS AN ID. The server writes it to the identity store first and
        keeps only the `PersonId`, so nothing on the read side ever carries these four.

        NULL on any other method, mirroring what the service clears — the form agreeing
        with the rule rather than depending on the service to tidy up after it.
      */
      externalPartyContactSalutation: lent ? contactSalutation.trim() || null : null,
      externalPartyContactFirstName: lent ? contactFirstName.trim() || null : null,
      externalPartyContactMiddleName: lent ? contactMiddleName.trim() || null : null,
      externalPartyContactLastName: lent ? contactLastName.trim() || null : null,
      externalPartyContactSuffix: lent ? contactSuffix.trim() || null : null,

      externalPartyProvince: lent ? address.province : null,
      externalPartyCityMunicipality: lent ? address.cityMunicipality : null,
      externalPartyBarangay: lent ? address.barangay : null,
      externalPartyStreetAddress: lent ? streetAddress.trim() || null : null,

      expectedReturnDate: issuanceType === ISSUANCE_TYPE.lend ? dueDate || null : null,
      /*
        RECEIVED BY IS NEVER SENT. The field left the form on 06 Oct 2026 and the request
        kept it, so restoring the question is an edit here rather than a migration — see
        where the state used to be. Null is what the row then carries, and the server
        derives no position for a signatory nobody named.
      */
      receivedByPersonnelId: null,
      issuedByPersonnelId: issuedById === '' ? null : issuedById,

      /*
        THE POSTS ARE NOT SENT. The server derives both off each signatory's substantive line
        of service, because a snapshot on a signed form that a caller can name is one a caller
        can misname.
      */
      // The LGU line is the SERVER's, off the accountable officer own unit — the request has
      // no field for it, which is what makes it unspoofable rather than merely unspoofed.
      fundCluster: custody ? fundCluster.trim() || null : null,
      // The form no longer asks — see where the field was. The endpoint still accepts one, so
      // a slip written by anything else keeps its own.
      referenceNumber: null,
      purpose: purpose.trim() || null,
      notes: notes.trim() || null,
      items: rows.map<CreateIssuanceLine>((row) => {
        const item = itemsById.get(row.inventoryItemId);

        return {
          inventoryItemId: row.inventoryItemId,

          // A serialized line sends no facility — the unit's own store is the answer, and a
          // second one here would be a different answer to a settled question.
          storageFacilityId: item?.tracksSerial ? null : row.storageFacilityId || null,
          quantity: item?.tracksSerial ? 1 : Number(row.quantity),
          stockUnitId: item?.tracksSerial ? row.stockUnitId || null : null,
        };
      }),
    };

    try {
      await issuancesApi.create(request);

      /*
        THE GROUPS THIS SLIP MOVED, read off the rows rather than off the response — the create
        answers with the issuance, and resolving each line back to its type would be a second
        question the picker feed has already answered.

        De-duplicated, because two lines of one type move that group once.
      */
      onSaved([
        ...new Set(
          rows
            .map((row) => itemsById.get(row.inventoryItemId)?.inventoryItemTypeId)
            .filter((typeId): typeId is string => typeId !== undefined),
        ),
      ]);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not issue the stock.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: 'issuance',
        /*
          THE UNIT HAS NO CONTROL OF ITS OWN ANY MORE, so a refusal naming it is pointed at the
          merged cell of the FIRST line — which lands the reader in the grid rather than
          nowhere, and nowhere is what they got before: the service names `stockUnitId` bare,
          with no line index in it, so the derived id has never resolved on this form.

          Naming the right LINE is not possible from what the server sends. That is a gap in the
          refusal rather than in this map, and it is the same one `storageFacilityId` has.
        */
        fieldIds: {
          stockUnitId: 'issuance-items-0-inventory-item-id',
          inventoryItemId: 'issuance-items-0-inventory-item-id',
        },
        stepOf: (field) => {
          const index = (key: StepKey) => Math.max(steps.indexOf(key), 0);

          if (field.startsWith('externalParty') || field === 'expectedReturnDate') return index('Loan Detail');
          if (field === 'accountablePersonnelId' || field.startsWith('issuedBy') || field === 'purpose' || field === 'notes' || field === 'fundCluster') return index('Summary');

          // Everything else — the method, the acknowledgement form, and every line — is here.
          return index('Items');
        },
        onStep: setStep,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Item Issuance"
      size="3xl"
      id="issuance-modal"
      autogenerate={{ onGenerate: autogenerate, disabled: saving }}
      confirm={
        isLastStep
          ? {
              id: 'issuance-confirm',
              checked: confirmed,
              onChange: setConfirmed,
              disabled: saving,
            }
          : undefined
      }
      footer={
        <>
          <Button id="issuance-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {safeStep > 0 ? (
            <Button
              id="issuance-back"
              variant="secondary"
              onClick={() => setStep((s) => s - 1)}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Tooltip disabledReason={stepValid ? null : missing} block={false}>
              <Button
                id="issuance-next"
                variant="secondary"
                onClick={() => setStep((s) => Math.min(s + 1, steps.length - 1))}
                disabled={!stepValid}
              >
                Next
              </Button>
            </Tooltip>
          ) : (
            <Tooltip disabledReason={canSave ? null : missing} block={false}>
              <Button
                id="issuance-submit"
                variant="primary"
                onClick={() => void save()}
                disabled={!canSave}
                loading={saving}
                loadingLabel="Submitting"
              >
                Submit
              </Button>
            </Tooltip>
          )}
        </>
      }
    >
      <Stepper
        id="issuance-steps"
        steps={steps.map((label) => ({ key: label, label }))}
        activeIndex={safeStep}
        completedKeys={new Set(steps.slice(0, safeStep))}
      />

      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {activeKey === 'Items' ? (
          <>
            {/*
              The METHOD leads because it decides the shape of everything below it — which
              fields are drawn, and which items the picker on the next step may offer at all.
            */}
            <Select
              id="issuance-issuance-type"
              label="Issuance method"
              required
              /*
                DISABLED FOR STATE where the kind leaves one answer, never hidden — and the
                description says why, which is the locked-parent rule: the reader chose the
                method by choosing whose row to press, so the picker has nothing left to ask.
              */
              description={
                methods.length === 1 && initialKind !== null && initialKind !== undefined
                  ? `A ${ITEM_KIND_LABEL[initialKind].toLowerCase()} can only be ${ISSUANCE_TYPE_LABEL[methods[0]].toLowerCase()}d, so the method is settled by the item you pressed Issue on.`
                  : 'What is happening to the stock. It decides which items can be issued and whether anything is expected back.'
              }
              value={String(issuanceType)}
              onChange={(next) => {
                setIssuanceType(Number(next) as IssuanceType);
                setRows([EMPTY_ROW()]);
                setSources({});
                setUnits({});
              }}
              options={methods.map((value) => ({
                value: String(value),
                label: ISSUANCE_TYPE_LABEL[value],
                sublabel: ISSUANCE_TYPE_HINT[value],
              }))}
              sort="none"
              clearable={false}
              disabled={saving || methods.length === 1}
              error={error?.fieldError('issuanceType')}
            />

            {/*
              THE FORM BEING SIGNED, and it is the second question because it scopes the
              first step answer rather than following from it.

              An ICS and a PAR are two different government forms carrying two different
              numbers, so ONE SLIP CANNOT HOLD BOTH — which is why the reader answers it here
              rather than the server reading it off the lines afterwards. Only stock recorded
              under the chosen form is offered on the next step.

              DRAWN ONLY ON A CUSTODY METHOD. A dispense signs neither form, and the server
              refuses an acknowledgement sent with one rather than ignoring it — so a control
              here would collect a value that can only ever be rejected.
            */}
            {custody ? (
              <Select
                id="issuance-acknowledgement-type"
                label="Asset acknowledgement type"
                required
                description="The property-accountability form the recipient signs. Only stock recorded under the chosen type can be issued."
                placeholder="Select ICS or PAR"
                value={acknowledgementType === '' ? '' : String(acknowledgementType)}
                onChange={(next) => {
                  /*
                    CHANGING THE FORM CLEARS THE LINES, exactly as changing the method does.
                    An ICS-class unit is not offerable under a PAR, so rows picked under the
                    old form would post stock the server refuses for a reason the reader
                    could not see on screen.
                  */
                  setAcknowledgementType(next === '' ? '' : (Number(next) as PropertyClassification));
                  setRows([EMPTY_ROW()]);
                  setSources({});
                  setUnits({});
                }}
                options={Object.values(PROPERTY_CLASSIFICATION).map((value) => ({
                  value: String(value),
                  label: PROPERTY_CLASSIFICATION_LABEL[value],
                  sublabel: PROPERTY_CLASSIFICATION_HINT[value],
                }))}
                sort="none"
                clearable={false}
                disabled={saving}
                error={error?.fieldError('acknowledgementType')}
              />
            ) : null}


            {/*
              THE METHOD IS EXPLAINED IN PLACE, not in a help tip — the reference prints the
              sentence under the pickers, where a reader choosing between three words reads
              what the one they picked means without hovering anything.
            */}
            <p className="text-xs text-slate-500">
              {ISSUANCE_TYPE_HINT[issuanceType]} Each item is drawn from wherever it is
              currently stocked — pick the source per line.
            </p>
            <p className="text-xs text-slate-500">
              Only stock available under this method is listed. A serialized item names its unit in the
              second column and takes that unit’s store; everything else is drawn from a store you
              pick, earliest expiry first.
            </p>

            <InlineAddTable
              headers={[
                'Item / Unit *',
                'Source facility',
                { label: 'Quantity', align: 'right' },
                { label: 'Item Worth', align: 'right' },
              ]}
              colWidths={[380, 200, 100, 130]}
              /*
                THE KEY IS v3 BECAUSE THE COLUMN SET CHANGED AGAIN — the Item and Unit columns
                merged into one on 29 Sep 2026. Stored widths are positional, so five columns’
                worth of saved values would be applied to the first four of four: a grid that
                overflows its container for every reader who had ever dragged a column, and for
                nobody who had not. It went v1 → v2 for the same reason.
              */
              resizeKey="issuance-lines.colWidths.v3"
              addButtonId="issuance-add-line"
              addDisabled={!rows.every(lineValid)}
              rowCount={rows.length}
              onAddRow={() => setRows((current) => [...current, EMPTY_ROW()])}
            >
              {rows.map((row, index) => {
                return (
                  <InlineRow
                    key={row.key}
                    idx={index + 1}
                    onRemove={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                  >
                    {/*
                      THE ITEM AND THE UNIT, in ONE cell — 29 Sep 2026.

                      They were two columns, on the reading that they are two questions: what is
                      leaving, and which one of it. For a QUANTITY item the second column could
                      never be answered and stated an em dash; for a SERIALIZED one it could
                      never be left alone, so the reader answered the first question only to be
                      asked a second that the first had already narrowed to a handful of rows.
                      One cell asks it once and searches both — see `lineOptions`.

                      The value carries both ids, so one control still fills two fields; nothing
                      about the REQUEST changed.
                    */}
                    <TdSelect
                      id={`issuance-items-${index}-inventory-item-id`}
                      ariaLabel="Item or unit"
                      value={row.inventoryItemId === '' ? '' : `${row.inventoryItemId}::${row.stockUnitId}`}
                      onChange={(next) => {
                        const [itemId = '', unitId = ''] = next.split('::');

                        setRow(row.key, {
                          inventoryItemId: itemId,
                          stockUnitId: unitId,
                          storageFacilityId: '',
                          quantity: '1',
                        });

                        // Still the SOURCES this loads, for a quantity line. A serialized row's
                        // store is its unit's, and the unit is already in `allUnits`.
                        void loadFor(itemId);
                      }}
                      options={optionsFor(row)}
                      remote
                      disabled={saving || loadingItems}
                    />

                    {/*
                      THE SOURCE — derived from the unit where there is one, picked otherwise.
                      A unit IS where it is, so a facility beside it would be a second answer to
                      a question the unit already settles.
                    */}
                    {itemsById.get(row.inventoryItemId)?.tracksSerial ? (
                      <TdSeamless>
                        <span className="truncate text-slate-500">
                          {row.stockUnitId === ''
                            ? 'Set by the chosen unit'
                            : (unitOf(row)?.storageFacilityName ?? 'No store')}
                        </span>
                      </TdSeamless>
                    ) : (
                      <TdSelect
                        id={`issuance-items-${index}-storage-facility-id`}
                        ariaLabel="Source facility"
                        value={row.storageFacilityId}
                        onChange={(next) => setRow(row.key, { storageFacilityId: next })}
                        options={(sources[row.inventoryItemId] ?? []).map((src) => ({
                          value: src.storageFacilityId,
                          label: src.storageFacilityName,
                          sublabel: `${src.available} available`,
                        }))}
                        remote
                        disabled={saving || !row.inventoryItemId}
                      />
                    )}

                    {/*
                      THE QUANTITY — a named unit is ONE, so the cell states it rather than
                      offering a field with a single legal answer.
                    */}
                    {itemsById.get(row.inventoryItemId)?.tracksSerial ? (
                      <TdSeamless align="right">
                        <span className="w-full text-right font-mono tabular-nums text-slate-500">1</span>
                      </TdSeamless>
                    ) : (
                      <TdNumber
                        id={`issuance-items-${index}-quantity`}
                        ariaLabel="Quantity"
                        value={row.quantity === '' ? null : Number(row.quantity)}
                        onChange={(next) =>
                          setRow(row.key, { quantity: next === null ? '' : String(next) })
                        }
                        min={1}
                        disabled={saving || !row.inventoryItemId}
                      />
                    )}

                    {/*
                      ITEM WORTH — READ-ONLY.

                      The label is the READER’S word, from 29 Sep 2026; the column reads
                      `unitCost` and the request carries none of this, so the rename is
                      contained to what is on screen — the label/entity split the personnel
                      form’s "Position" picker already takes.

                      A cost is never edited while issuing, which is the reference’s own rule.
                      There is no cost on the catalog row in this product, so a quantity line
                      has none to state and reads as an em dash.
                    */}
                    <TdSeamless align="right">
                      {(() => {
                        const price =
                          row.stockUnitId === '' ? EMPTY_MONEY : formatMoney(unitOf(row)?.unitCost ?? null);

                        return (
                          <span
                            className="w-full truncate text-right font-mono tabular-nums text-slate-500"
                            title={price}
                          >
                            {price}
                          </span>
                        );
                      })()}
                    </TdSeamless>
                  </InlineRow>
                );
              })}
            </InlineAddTable>

            <p className="text-xs text-slate-500">
              Saving moves the stock. There is no draft stage — a custody issuance is undone by
              recording a return, and a dispense cannot be undone at all.
            </p>
          </>
        ) : activeKey === 'Loan Detail' ? (
          <>
            {/*
              THE LOAN DETAIL STEP — the reference’s own `needsLoanDetailStep`, drawn only where there
              is an external party to name. A dispense and an assign go to an employee, so the
              step would hold nothing, and it is not offered at all rather than drawn empty.
            */}
            <p className="text-xs text-slate-500">
              Who the stock is going out to, and when it is due back.
            </p>

            {/*
              WHO THE STOCK WENT TO, as a SECTION rather than fields in the run.

              It is eight values under one heading, which is what `<FormFieldset>` is for: at
              the run's own rhythm the first label reads as belonging to the control beneath it
              rather than as a heading over the several that follow.

              DRAWN ONLY ON A LEND. Every field in here is refused by the server on an internal
              method — a dispense goes to an employee and an office, so a barangay on one is
              data nobody can interpret.
            */}
            <FormFieldset legend="Lent to">
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  id="issuance-external-party-name"
                  label="Organization"
                  required
                  description="The organization the stock goes to — not a person's name."
                  value={externalParty}
                  onChange={(e) => setExternalParty(e.target.value)}
                  maxLength={200}
                  disabled={saving}
                  error={error?.fieldError('externalPartyName')}
                />

                <TextField
                  id="issuance-external-party-contact"
                  label="Contact number"
                  description="A number or address to reach them on."
                  value={externalContact}
                  onChange={(e) => setExternalContact(e.target.value)}
                  maxLength={100}
                  disabled={saving}
                  error={error?.fieldError('externalPartyContact')}
                />
              </div>

              {/*
                THE CONTACT PERSON, and the three-column name row is the one place a form
                grid goes to three: first, middle and last are ONE fact split across three
                controls, so they read as a line rather than wrapping.

                The surname and its suffix join into one bordered control, because "Dela Cruz
                Jr." is one name — `<FieldGroup>`, not two fields three rows apart.
              */}
              <div className="grid gap-4 sm:grid-cols-3">
                <FieldGroup
                  label="Contact first name"
                  labelFor="issuance-external-party-contact-first-name"
                  description="The narrow box on the left is a title — Mr., Dr., Atty. It is optional, and it is not part of the name."
                  segments={[
                    {
                      id: 'issuance-external-party-contact-salutation',
                      label: 'Salutation',
                      value: contactSalutation,
                      onChange: setContactSalutation,
                      options: SALUTATION_OPTIONS,
                      disabled: saving,
                      // The addon gives way, never the grid — a name row is three equal columns.
                      width: SALUTATION_SEGMENT_WIDTH,
                      emptyLabel: SALUTATION_EMPTY_LABEL,
                    },
                    {
                      id: 'issuance-external-party-contact-first-name',
                      label: 'First name',
                      value: contactFirstName,
                      onChange: setContactFirstName,
                      maxLength: 100,
                      error: error?.fieldError('externalPartyContactFirstName'),
                      disabled: saving,
                    },
                  ]}
                />

                <TextField
                  id="issuance-external-party-contact-middle-name"
                  label="Middle name"
                  value={contactMiddleName}
                  onChange={(e) => setContactMiddleName(e.target.value)}
                  maxLength={100}
                  disabled={saving}
                  error={error?.fieldError('externalPartyContactMiddleName')}
                />

                <FieldGroup
                  label="Last name"
                  description="Give a first and a last name, or leave the contact blank."
                  segments={[
                    {
                      id: 'issuance-external-party-contact-last-name',
                      label: 'Last name',
                      value: contactLastName,
                      onChange: setContactLastName,
                      maxLength: 100,
                      error: error?.fieldError('externalPartyContactLastName'),
                      disabled: saving,
                    },
                    {
                      id: 'issuance-external-party-contact-suffix',
                      label: 'Suffix',
                      value: contactSuffix,
                      onChange: setContactSuffix,
                      maxLength: 20,
                      width: 'w-20 shrink-0',
                      error: error?.fieldError('externalPartyContactSuffix'),
                      disabled: saving,
                    },
                  ]}
                />
              </div>

              {/*
                THE ADDRESS IS ONE SEARCH, and it is FULL WIDTH and stacked.

                Both values are long — a path reads "Rizal, Antipolo, Bagong Nayon" and a
                street line carries a lot — so a half-width box truncates the one thing the
                reader is checking. It writes NAMES rather than ids, because an address is what
                somebody stated at an instant.
              */}
              <AddressPicker
                id="issuance-external-party-address"
                description="Where they are. A path may stop at the province or the city."
                value={address}
                onChange={setAddress}
                disabled={saving}
                error={error?.fieldError('externalPartyProvince')}
              />

              <TextField
                id="issuance-external-party-street-address"
                label="Lot No / Street / Bldg"
                description="One line, in the order their own address uses."
                value={streetAddress}
                onChange={(e) => setStreetAddress(e.target.value)}
                maxLength={300}
                disabled={saving}
                error={error?.fieldError('externalPartyStreetAddress')}
              />
            </FormFieldset>

            <DateField
              id="issuance-expected-return-date"
              label="Expected Return Date"
              required
              description="When the stock is due to return. A lend past this date reads as overdue."
              min={todayCalendarDate()}
              value={dueDate}
              onChange={setDueDate}
              disabled={saving}
              error={error?.fieldError('expectedReturnDate')}
            />
          </>
        ) : (
          <>
            {/*
              THE SUMMARY COLLECTS; it does not re-state.

              A stepped create form here does not end on a read-only recap of what was just
              typed — that is the same values in two slots one click apart, and it is not a
              second chance to edit. What earns this step is that it asks for what neither of
              the first two do: who signed, and why.
            */}
            <p className="text-xs text-slate-500">
              Who signed the slip, and what it was for. Everything above is already settled —
              ticking the confirmation releases the stock.
            </p>

            {/*
              THE ACCOUNTABLE OFFICER is asked HERE rather than on the method step — the
              reference’s own placement. One officer signs a slip however many items it
              carries, so it belongs beside the signatory rather than beside the question
              that decides which items may go on it at all.

              IT SITS IN LINE WITH ISSUED BY — product call, 06 Oct 2026, in the reader own
              words: *move the issued by in line with accountable*. The two are the whole
              signature block now that Received by is gone, so a row of two reads as one
              question rather than as two fields a line apart.

              THE GRID CARRIES BOTH OR ONE, and `sm:grid-cols-2` is conditional for that
              reason: on a DISPENSE there is no accountable officer, and a lone field in a
              half-width cell reads as a section with one item in it — which is the stranded
              field CLAUDE.md names, down a column rather than across a row.
            */}
            <div className={custody ? 'grid gap-4 sm:grid-cols-2' : 'grid gap-4'}>
            {custody && canReadPersonnel ? (
              <Select
                id="issuance-accountable-personnel-id"
                label="Accountable employee"
                required
                description="Who signs for this stock. One officer per slip, however many items it carries."
                placeholder="Choose an employee"
                value={accountableId}
                onChange={setAccountableId}
                options={staff.map((s) => ({
                  value: s.id,
                  label: s.name,

                  // The office they sit in — see where `unit` is filled.
                  sublabel: s.unit ?? 'No unit',
                }))}
                remote
                disabled={saving}
                error={error?.fieldError('accountablePersonnelId')}
              />
            ) : null}

            {/*
              THE ONE SIGNATORY LEFT. Optional on every method and drawn on every method — a
              dispense is released by somebody just as an assignment is, so withholding it
              from the simplest release would make the slip unable to say who was at the
              counter.

              It is a PERSONNEL ID and never a name: the row stores the id, the profile
              resolves it in one batch, and the list cannot sort by it. That is the identity
              split rather than a limitation of this form.
            */}
            {canReadPersonnel ? (
              <Select
                id="issuance-issued-by-personnel-id"
                label="Issued by"
                description="Who released it from the store. Defaults to you where your account is linked to an employee record — on the personnel profile's Account card. Left empty otherwise."
                placeholder="Not recorded"
                value={issuedById}
                onChange={setIssuedById}
                options={staff.map((s) => ({
                  value: s.id,
                  label: s.name,
                  sublabel: s.unit ?? 'No unit',
                }))}
                remote
                disabled={saving}
                error={error?.fieldError('issuedByPersonnelId')}
              />
            ) : null}
            </div>

            {/*
              PURPOSE IS FULL WIDTH, outside the grid — the REMARKS rule, which this field is
              the issuance’s own instance of: it is the longest value on the form and the one
              most likely to be a sentence, so half a row is the box that truncates while the
              field beside it needed none of the room.

              REFERENCE is GONE — product call, 28 Sep 2026. A slip carries its own permanent
              `ISS-` number, and a second free-typed document code beside it was a field that
              collected a value nothing reads and nothing can follow.
            */}
            <TextField
              id="issuance-purpose"
              label="Purpose"
              description="What the stock is for."
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              maxLength={500}
              disabled={saving}
              error={error?.fieldError('purpose')}
            />

            <TextField
              id="issuance-notes"
              label="Notes"
              description="Optional."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              disabled={saving}
              error={error?.fieldError('notes')}
            />

            {/*
              WHAT IS PRINTED AT THE HEAD OF THE FORM.

              CUSTODY ONLY, and the server refuses a fund cluster on any other method rather
              than ignoring it: a dispense signs no government form, so there is no header for
              it to carry. That is the same pair the acknowledgement type on step 1 already
              has.

              THE LGU LINE IS GONE FROM THE FORM — product call, 06 Oct 2026. It is derived
              from the accountable officer own unit, which is what closes the gap this comment
              used to record: the source seeds its own from an organization-profile row this
              product does not have, and a seeded guess would have been a value nobody chose
              sitting on every signed slip. The office the officer actually sits in is a value
              somebody did choose.
            */}
            {custody ? (
              <FormFieldset legend="ICS / PAR form details">
                {/*
                  THE FUND CLUSTER IS A DROPDOWN of the three PPA expense classes — product
                  call, 06 Oct 2026, in the reader own words: *make the fund cluster dropdown,
                  use the fund source types from the PPA: Personel/mooe/capital outlay etc.*

                  ONE DEFINITION, from `lib/expenseClass` — the same map the PPA line items
                  draw, which is why it moved out of the investment-planning slice rather than
                  being retyped here. Two copies of one vocabulary is how a module shows a
                  reader two words for one concept.

                  `sort="none"` because the three read in the FORM order — Personnel Services,
                  MOOE, Capital Outlay. Alphabetically they would read Capital Outlay, MOOE,
                  Personnel Services: alphabetical order pretending to be the form own.

                  THE COLUMN STAYS FREE TEXT and the VALUE is the label. Nothing branches on
                  this or aggregates by it — it is printed prose — and two slips recorded
                  before this carry genuine fund clusters a closed server-side set would have
                  made unwritable. The picker is what constrains a reader.
                */}
                <Select
                  id="issuance-fund-cluster"
                  label="Fund cluster"
                  description="What this release is charged to, from the budget expense classes. Each article carries its own fund as well."
                  placeholder="Not recorded"
                  value={fundCluster}
                  onChange={setFundCluster}
                  options={PPA_EXPENSE_CLASS_ORDER.map((value) => ({
                    value: PPA_EXPENSE_CLASS_LABEL[value],
                    label: PPA_EXPENSE_CLASS_LABEL[value],
                  }))}
                  sort="none"
                  disabled={saving}
                  error={error?.fieldError('fundCluster')}
                />

                {/*
                  THE POST AND THE OFFICE ARE NOT ASKED FOR, and the absence is deliberate
                  rather than an omission the reader should fill in.

                  The source puts its signatory positions on its own create request and fills
                  them from whichever employee was picked. A snapshot on a signed government
                  form that a caller can name is one a caller can misname, so here the server
                  derives them off the substantive line of service — the same derivation the
                  personnel list reports, which is why the pickers above already state each
                  employee's unit.

                  THE LGU LINE JOINED THEM on 06 Oct 2026: it is the accountable officer own
                  office, off that same line of service. The sentence below says so, because a
                  value printed at the head of a signed form and asked for nowhere is one the
                  reader is entitled to be told about.
                */}
                <p className="text-xs text-slate-500">
                  The LGU / office line is the accountable employee&apos;s own unit, and each
                  signatory&apos;s post is their own. Both are recorded automatically, as they
                  stand today.
                </p>
              </FormFieldset>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}
